import React from 'react';
import { Clock, MapPin, FileText, DollarSign, Calendar, CheckCircle, Receipt } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { formatTime, formatDate } from '../../../utils/jobGrouping';
import { useUserPreferences } from '../../../contexts/UserPreferencesContext';

/**
 * SingleJobRow
 *
 * Consolidated row for one-time jobs (single instance) in earnings mode.
 * Combines job info + instance info + earnings info in a single row
 * to avoid the confusing Job Row → Instance Row hierarchy for one-off jobs.
 */
const SingleJobRow = ({
  job,
  instance,
  timesheet,
  colorScheme,
  isLast = false,
  isHighlighted = false,
  showInvoice = false,
  onViewInvoice,
  // Payment confirmation props
  onConfirmPayment,
  isConfirmingPayment = false,
  // View payment proof
  onViewPaymentProof
}) => {
  const { preferences } = useUserPreferences();

  const getPriorityBadge = () => {
    const priority = job?.priority?.toLowerCase();
    const styles = {
      high: 'bg-red-500/20 text-red-400',
      medium: 'bg-yellow-500/20 text-yellow-400',
      low: 'bg-green-500/20 text-green-400'
    };

    return (
      <span className={cn(
        'px-2 py-0.5 text-xs font-medium rounded-full uppercase',
        styles[priority] || 'bg-gray-500/20 text-text-secondary'
      )}>
        {priority || 'normal'}
      </span>
    );
  };

  // Payment status badge - the only status that matters for earnings
  const getPaymentStatusBadge = () => {
    // Check both timesheet and instance status, prefer 'paid' if either has it
    const timesheetStatus = timesheet?.status;
    const instanceStatus = instance?.status;
    const status = (timesheetStatus === 'paid' || instanceStatus === 'paid')
      ? 'paid'
      : (timesheetStatus || instanceStatus || 'pending');

    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      approved: 'bg-green-500/20 text-green-400 border-green-500/30',
      paid: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/30'
    };

    const labels = {
      pending: 'Pending',
      approved: 'Approved',
      paid: 'Paid',
      rejected: 'Rejected'
    };

    return (
      <span className={cn(
        'px-2 py-0.5 text-xs font-medium rounded-full border',
        styles[status] || styles.pending
      )}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <div
      id={`job-card-${instance?.id}`}
      className={cn(
        'pl-6 pr-3 py-3 transition-all duration-300',
        colorScheme?.job,
        !isLast && 'border-b border-dashed border-accent/40',
        isHighlighted && 'ring-2 ring-orange-500 ring-offset-2 ring-offset-background'
      )}
    >
      <div className="flex items-start justify-between gap-3">
        {/* Left side: Job and instance details */}
        <div className="flex-1 min-w-0">
          {/* Top row: Job name and priority */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-text-primary font-medium text-sm truncate">
              {job?.job_name || 'Unnamed Job'}
            </span>
            {getPriorityBadge()}
            <span className="text-text-secondary text-xs">One-off</span>
          </div>

          {/* Middle row: Date, time, location */}
          <div className="flex items-center gap-3 mt-1.5 text-xs text-text-secondary flex-wrap">
            <div className="flex items-center">
              <Calendar size={11} className="mr-1" />
              <span>{formatDate(instance?.scheduled_date, preferences.date_format)}</span>
            </div>
            {instance?.scheduled_time && (
              <div className="flex items-center">
                <Clock size={11} className="mr-1" />
                <span>{formatTime(instance.scheduled_time, preferences.time_format)}</span>
              </div>
            )}
            <div className="flex items-center">
              <MapPin size={11} className="mr-1 flex-shrink-0" />
              <span className="truncate max-w-[200px]">{job?.job_site?.address || job?.location_address || 'No location'}</span>
            </div>
          </div>

          {/* Bottom row: Earnings info */}
          {timesheet && (
            <div className="flex items-center gap-3 mt-1.5 text-sm">
              <div className="flex items-center text-green-400">
                <DollarSign size={12} className="mr-0.5" />
                <span className="font-medium">{job?.payment_amount || '0'}</span>
                <span className="text-text-secondary ml-0.5 text-xs">
                  {job?.payment_type === 'hourly' ? '/hr' : ''}
                </span>
              </div>
              <span className="text-text-secondary">
                {parseFloat(timesheet.total_hours || 0).toFixed(1)}h worked
              </span>
              <span className="text-green-400 font-medium">
                ${parseFloat(timesheet.amount_earned || 0).toFixed(2)} earned
              </span>
              {getPaymentStatusBadge()}
            </div>
          )}
        </div>

        {/* Right side: Proof + Confirm Button + Invoice Button */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* View Payment Proof button - only shows for paid items */}
          {timesheet && (() => {
            const timesheetStatus = timesheet?.status;
            const instanceStatus = instance?.status;
            const isPaid = timesheetStatus === 'paid' || instanceStatus === 'paid';

            if (!isPaid || !onViewPaymentProof) return null;

            return (
              <button
                onClick={() => onViewPaymentProof(timesheet.id)}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-all bg-purple-500/20 hover:bg-purple-500/30 text-purple-400"
                title="View payment proof"
              >
                <Receipt size={12} />
                <span className="hidden sm:inline">Proof</span>
              </button>
            );
          })()}
          {/* Payment confirmation button - only shows for paid items */}
          {timesheet && (() => {
            const timesheetStatus = timesheet?.status;
            const instanceStatus = instance?.status;
            const isPaid = timesheetStatus === 'paid' || instanceStatus === 'paid';
            const isConfirmed = timesheet?.contractor_confirmed_received;

            if (!isPaid || !onConfirmPayment) return null;

            return (
              <button
                onClick={() => onConfirmPayment(timesheet.id, !isConfirmed)}
                disabled={isConfirmingPayment}
                className={cn(
                  'flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-all',
                  isConfirmed
                    ? 'bg-green-500 hover:bg-green-600 text-white'
                    : 'bg-orange-500 hover:bg-orange-600 text-white',
                  isConfirmingPayment && 'opacity-50 cursor-not-allowed'
                )}
                title={isConfirmed ? 'Click to unmark as received' : 'Confirm you received this payment'}
              >
                <CheckCircle size={12} />
                <span className="hidden sm:inline">{isConfirmed ? 'Received' : 'Confirm'}</span>
              </button>
            );
          })()}
          {showInvoice && onViewInvoice && (
            <button
              onClick={onViewInvoice}
              className="flex items-center gap-1 px-2 py-1 bg-surface hover:bg-surface-light text-text-secondary hover:text-text-primary text-xs rounded transition-colors"
              title="View Invoice"
            >
              <FileText size={12} />
              <span className="hidden sm:inline">Invoice</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SingleJobRow;
